import React from 'react';
import styled from 'styled-components';
import { IComputedAggregate, IMetric } from '../../../data/types';
import { roundValue } from '../../../utils/math';
import { Column } from '../../layout/Column';
import { Card } from '../../shared/card/Card';
import { CardBody } from '../../shared/card/CardBody';
import { CardHeader } from '../../shared/card/CardHeader';
import { GraphContainer } from '../../shared/graphs/GraphContainer';
import { TrendGraph } from '../../shared/graphs/TrendGraph';
import { NoResults } from '../../shared/NoResults';
import { Notable } from '../../shared/pdf/Notable';
import { EPdfMode } from '../../shared/pdf/Pdf';
import { MetricHeader } from './MetricHeader';

interface IProps {
  testType: string;
  tags: string[];
  metric: IMetric;
  aggregate: IComputedAggregate;
  timeframe: string;
  sessions: IComputedAggregate[];
  pdfMode?: EPdfMode;
  isValid?: boolean;
  whiskers: boolean;
}

export const TrendCard = (props: IProps): React.ReactElement => {
  /// determine if in pdf mode
  const isPdf =
    props.pdfMode === EPdfMode.PREVIEW || props.pdfMode === EPdfMode.PRINT;

  return (
    <PdfCardWrapper className='avoid-break' pdf={isPdf} isValid={props.isValid}>
      <Notable pdf={isPdf}>
        <Card
          inverse={props.pdfMode === EPdfMode.PRINT}
          style={isPdf ? { overflow: 'visible', height: 'auto' } : undefined}
        >
          <Column>
            <CardHeader inverse={props.pdfMode === EPdfMode.PRINT}>
              <MetricHeader
                metricLabel={props.metric.label}
                metricUnits={
                  props.aggregate?.data?.[props.metric.id]
                    ? props.aggregate.data[props.metric.id].units
                    : ''
                }
                tags={props.tags}
                testType={props.testType}
                timeframe={props.timeframe}
                min={
                  props.aggregate?.data?.[props.metric.id]
                    ? roundValue(
                        props.aggregate.data[props.metric.id].bottomValues[0],
                        props.aggregate.data[props.metric.id].roundingPrecision
                      ) + props.aggregate.data[props.metric.id].units
                    : 'N/A'
                }
                max={
                  props.aggregate?.data?.[props.metric.id]
                    ? roundValue(
                        props.aggregate.data[props.metric.id].topValues[0],
                        props.aggregate.data[props.metric.id].roundingPrecision
                      ) + props.aggregate.data[props.metric.id].units
                    : 'N/A'
                }
                avg={
                  props.aggregate?.data?.[props.metric.id]
                    ? roundValue(
                        props.aggregate.data[props.metric.id].mean,
                        props.aggregate.data[props.metric.id].roundingPrecision
                      ) + props.aggregate.data[props.metric.id].units
                    : 'N/A'
                }
              />
            </CardHeader>
            <CardBody withoutFooter notScrollable={isPdf}>
              <GraphContainer
                inverse={props.pdfMode === EPdfMode.PRINT}
                style={isPdf ? { height: 'auto' } : undefined}
              >
                {props.aggregate?.data &&
                props.aggregate?.data?.[props.metric.id] ? (
                  <TrendGraph
                    selectedMetricID={props.metric.id}
                    sessions={props.sessions}
                    pdfMode={props.pdfMode}
                    whiskers={props.whiskers}
                  />
                ) : (
                  <EmptyState
                    tags={props.tags}
                    testType={props.testType}
                    testsExist={!!props.aggregate?.data}
                    metric={props.metric}
                  />
                )}
              </GraphContainer>
            </CardBody>
          </Column>
        </Card>
      </Notable>
    </PdfCardWrapper>
  );
};

interface IEmptyStateProps {
  testType: string;
  tags: string[];
  testsExist: boolean;
  metric: IMetric;
}
const EmptyState = (props: IEmptyStateProps): React.ReactElement => {
  if (props.testsExist) {
    return (
      <NoResults
        style={{ fontSize: '0.8em' }}
        message={`${props.metric.label} was not calculated for any of the ${props.testType}s
					during the selected timeframe.`}
      />
    );
  } else if (props.tags?.length > 1) {
    return (
      <NoResults
        style={{ fontSize: '0.8em' }}
        message={`
					There are no ${props.testType}s with the ${props.tags.join('')} tag
					during the selected timeframe.`}
      />
    );
  } else if (props.tags?.length > 0) {
    return (
      <NoResults
        style={{ fontSize: '0.8em' }}
        message={`There are no ${props.testType}s with the ${props.tags.join(
          ', '
        )} tags
					during the selected timeframe.`}
      />
    );
  } else {
    return (
      <NoResults
        style={{ fontSize: '0.8em' }}
        message={`There are no ${props.testType}s during the selected timeframe.`}
      />
    );
  }
};

const PdfCardWrapper = styled.div<{ pdf?: boolean; isValid?: boolean }>`
  flex: 1;
  padding: 2px;
  margin-top: ${(props: { pdf?: boolean }): string =>
    props.pdf ? 'var(--grid-gap)' : '0px'};
  @media (max-width: 990px) {
    margin-top: var(--grid-gap);
  }
  display: ${(props: { pdf?: boolean; isValid?: boolean }): string =>
    props.pdf && !props.isValid ? 'none' : 'block'};
  @media (min-width: 991px) {
    &:first-child {
      margin-left: ${(props: { pdf?: boolean }): string =>
        props.pdf ? '0px' : 'var(--grid-gap)'};
      margin-right: ${(props: { pdf?: boolean }): string =>
        props.pdf ? '0px' : 'var(--grid-gap)'};
    }
    &:last-child {
      margin-right: ${(props: { pdf?: boolean }): string =>
        props.pdf ? '0px' : 'var(--grid-gap)'};
    }
  }
`;
